import { Schema } from "effect";

// Chat API contract shared by the StationChat Durable Object, the backend
// routes, and the frontend chat tab. Rows form an append-only, monotonically
// sequenced per-thread log; the log is the only durable conversation state.

export const ChatRowKind = /* @__PURE__ */ (() => Schema.Literals(["user", "assistant", "tool_call", "tool_result", "approval_request", "approval_decision", "proposal", "proposal_outcome", "turn_end"]))();
export type ChatRowKind = typeof ChatRowKind.Type;

export const ChatTurnEndReason = /* @__PURE__ */ (() => Schema.Literals(["completed", "awaiting_approval", "cancelled", "limit_reached", "provider_error", "not_configured"]))();
export type ChatTurnEndReason = typeof ChatTurnEndReason.Type;

export const ChatRowBase = /* @__PURE__ */ (() => Schema.Struct({
  sequence: Schema.Number,
  threadId: Schema.String,
  kind: ChatRowKind,
  createdAt: Schema.Number,
}))();
export type ChatRowBase = typeof ChatRowBase.Type;

export const CHAT_ATTACHMENT_LIMITS = Object.freeze({
  count: 8,
  filenameChars: 240,
  // Inline data is persisted in a single SQLite row. One MiB of raw data
  // expands to roughly 1.4 MB as base64, leaving headroom under the platform's
  // 2 MB maximum row size for message text and attachment metadata.
  bytesPerAttachment: 1024 * 1024,
  totalBytes: 1024 * 1024
});

/** Maximum encoded body accepted by the chat message endpoint. */
export const CHAT_MESSAGE_REQUEST_MAX_BYTES = 1_500_000;

export const CHAT_ATTACHMENT_MIME_TYPES = Object.freeze([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain"
] as const);

export const ChatAttachmentMime = /* @__PURE__ */ (() => Schema.Literals(CHAT_ATTACHMENT_MIME_TYPES))();
export type ChatAttachmentMime = typeof ChatAttachmentMime.Type;

/** A bounded inline attachment carried by a user row and replayed to the model. */
export const ChatAttachment = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String,
  filename: Schema.String,
  mime: ChatAttachmentMime,
  dataUrl: Schema.String,
}))();
export type ChatAttachment = typeof ChatAttachment.Type;

export const ChatMessageRequest = /* @__PURE__ */ (() => Schema.Struct({
  text: Schema.String,
  attachments: Schema.Array(ChatAttachment).pipe(Schema.optional),
}))();
export type ChatMessageRequest = typeof ChatMessageRequest.Type;

export const ChatUserRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("user"),
  text: Schema.String,
  attachments: Schema.Array(ChatAttachment).pipe(Schema.optional),
}))();
export type ChatUserRow = typeof ChatUserRow.Type;

export const ChatAssistantRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("assistant"),
  text: Schema.String,
}))();
export type ChatAssistantRow = typeof ChatAssistantRow.Type;

export const ChatToolCallRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("tool_call"),
  callId: Schema.String,
  tool: Schema.String,
  args: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)),
  // Set when yolo mode ran a call that would otherwise have asked. The
    // transcript must still show which actions no human confirmed.
  autoApproved: Schema.Boolean.pipe(Schema.optional),
}))();
export type ChatToolCallRow = typeof ChatToolCallRow.Type;

export const ChatToolResultRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("tool_result"),
  callId: Schema.String,
  tool: Schema.String,
  isError: Schema.Boolean,
  resultText: Schema.String,
}))();
export type ChatToolResultRow = typeof ChatToolResultRow.Type;

export const ChatApprovalRequestRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("approval_request"),
  approvalId: Schema.String,
  callId: Schema.String,
  tool: Schema.String,
  args: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)),
  destructive: Schema.Boolean,
}))();
export type ChatApprovalRequestRow = typeof ChatApprovalRequestRow.Type;

export const ChatApprovalDecisionRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("approval_decision"),
  approvalId: Schema.String,
  /** Present on new rows so an invisible yolo intent can attribute its call. */
  callId: Schema.String.pipe(Schema.optional),
  approved: Schema.Boolean,
  // Set when yolo made this decision without human confirmation.
  autoApproved: Schema.Boolean.pipe(Schema.optional),
}))();
export type ChatApprovalDecisionRow = typeof ChatApprovalDecisionRow.Type;

export const ChatApprovalDecisionResponse = /* @__PURE__ */ (() => Schema.Struct({
  ok: Schema.Literal(true),
  /** True only when this decision started/resumed the model loop. */
  resumed: Schema.Boolean,
  /** Authoritative server state after applying the decision. */
  turnActive: Schema.Boolean,
  replayed: Schema.Boolean.pipe(Schema.optional),
}))();
export type ChatApprovalDecisionResponse = typeof ChatApprovalDecisionResponse.Type;

export const ChatProposalRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("proposal"),
  proposalId: Schema.String,
  worktreeId: Schema.String,
  summary: Schema.String,
  fileCount: Schema.Number,
}))();
export type ChatProposalRow = typeof ChatProposalRow.Type;

export const ChatProposalOutcomeRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("proposal_outcome"),
  proposalId: Schema.String,
  outcome: Schema.Literals(["merged", "discarded", "merge_failed", "discard_failed"]),
  detail: Schema.String.pipe(Schema.optional),
}))();
export type ChatProposalOutcomeRow = typeof ChatProposalOutcomeRow.Type;

export const ChatTurnEndRow = /* @__PURE__ */ (() => Schema.Struct({
  ...ChatRowBase.fields,
  kind: Schema.Literal("turn_end"),
  reason: ChatTurnEndReason,
  detail: Schema.String.pipe(Schema.optional),
}))();
export type ChatTurnEndRow = typeof ChatTurnEndRow.Type;

export const ChatRow = /* @__PURE__ */ (() => Schema.Union([ChatUserRow, ChatAssistantRow, ChatToolCallRow, ChatToolResultRow, ChatApprovalRequestRow, ChatApprovalDecisionRow, ChatProposalRow, ChatProposalOutcomeRow, ChatTurnEndRow]))();
export type ChatRow = typeof ChatRow.Type;

export const ChatThreadSummary = /* @__PURE__ */ (() => Schema.Struct({
  threadId: Schema.String,
  title: Schema.String,
  createdAt: Schema.Number,
  lastSequence: Schema.Number,
  archived: Schema.Boolean,
}))();
export type ChatThreadSummary = typeof ChatThreadSummary.Type;

// Provisional state streams alongside durable rows; it may be discarded and is
// always superseded by durable rows. A changed `streamGeneration` means the
// server restarted mid-turn and the client must drop provisional text.
export const ChatProvisional = /* @__PURE__ */ (() => Schema.Struct({
  turnActive: Schema.Boolean,
  text: Schema.String,
  // Reasoning output streams here only. It is never written to a durable row,
    // so it cannot survive a restart and never re-enters a replayed request.
  reasoning: Schema.String,
}))();
export type ChatProvisional = typeof ChatProvisional.Type;

export const ChatTailResponse = /* @__PURE__ */ (() => Schema.Struct({
  rows: Schema.Array(ChatRow).pipe(Schema.mutable),
  nextAfterSequence: Schema.Number,
  streamGeneration: Schema.Number,
  provisional: ChatProvisional,
}))();
export type ChatTailResponse = typeof ChatTailResponse.Type;

export const ChatConfigResponse = /* @__PURE__ */ (() => Schema.Struct({
  configured: Schema.Boolean,
  model: Schema.String,
  // Opt-in, per station, default off: run every tool without asking.
  yolo: Schema.Boolean,
}))();
export type ChatConfigResponse = typeof ChatConfigResponse.Type;

export const ChatModelOption = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  /**
     * Whether the forwarded `Authorization` header carries the gateway client
     * key. Custom providers whose upstream credentials live in the gateway's
     * stored Access headers ignore that value, so it is sent as a placeholder.
     */
  forwardsClientKey: Schema.Boolean,
}))();
export type ChatModelOption = typeof ChatModelOption.Type;

export const CHAT_MODELS: readonly ChatModelOption[] = [
  {
    id: "custom-medsimples-clever-schrodinger/deepseek-v4-flash",
    label: "DeepSeek V4 Flash",
    forwardsClientKey: false
  },
  {
    id: "custom-medsimples/deepseek-v4-flash-0731-spark",
    label: "DeepSeek V4 Flash Spark",
    forwardsClientKey: false
  },
  { id: "custom-zai/glm-5.2", label: "GLM 5.2", forwardsClientKey: true }
];

export function chatModelOption(id: string): ChatModelOption | undefined {
  return CHAT_MODELS.find((model) => model.id === id);
}

export const CHAT_DEFAULT_MODEL =
  "custom-medsimples-clever-schrodinger/deepseek-v4-flash";

export const CHAT_WORKTREE_PREFIX = "chat/";

export function chatWorktreeName(threadId: string): string {
  return `${CHAT_WORKTREE_PREFIX}${threadId}`;
}
